import { memo, useEffect, useRef, useState } from "react";
import * as rpc from "@1games-ru/rage-rpc";
import { Button, Card, TextField } from "@radix-ui/themes";
import { createBlurChangeNumber, createChangeNumber } from "@/core/helpers/smartNumberSetter.ts";
import AnimationDictionaryComponent from "@/domains/dev/animationPlayer/AnimationDictionaryComponent.tsx";

function AnimationPlayerContainer() {
  const portalRef = useRef(null);

  const [usesDictionary, setUsesDictionary] = useState<boolean>(false);
  const [dictionary, setDictionary] = useState<string>("abigail_mcs_1_concat-9");
  const [name, setName] = useState<string>("csb_abigail_dual-9");
  const [speed, setSpeed] = useState<number | string>(1);
  const [speedMultiplier, setSpeedMultiplier] = useState<number | string>(1);
  const [duration, setDuration] = useState<number | string>(-1);
  const [flag, setFlag] = useState<number | string>(1);
  const [playbackRate, setPlaybackRate] = useState<number | string>(1);
  const [animations, setAnimations] = useState<object | null>(null);
  const [response, setResponse] = useState<object | null>(null);

  const getComposedName = (): string => [dictionary, name].filter((v) => v && v.length).join(" : ");
  const setComposedName = (value: string): void => {
    const [targetDictionary, targetName] = value.split(" ").filter((v) => v !== ":");

    setDictionary(targetDictionary && targetDictionary.length ? targetDictionary : "");
    setName(targetName && targetName.length ? targetName : "");
  };

  useEffect(() => {
    console.log("render penes");
  }, []);

  const handleSubmit = (event: React.FormEvent) => {
    event.preventDefault();

    const animation: object = {
      dictionary,
      name,
      speed,
      speedMultiplier,
      duration,
      flag,
      playbackRate,
      lockX: false,
      lockY: false,
      lockZ: false,
    };

    rpc
      .callServer("DEV_PLAY_ANIMATION", { animation })
      .then((res: any): void => {
        console.log({ res });
        setResponse(res);
      })
      .catch((e: any): void => {
        console.log({ e });
        setResponse([JSON.stringify(e)]);
      });
  };

  const stop = () => {
    rpc
      .callServer("DEV_STOP_ANIMATION", {})
      .then((res: any): void => {
        console.log({ res });
        setResponse(res);
      })
      .catch((e: any): void => {
        console.log({ e });
        setResponse([JSON.stringify(e)]);
      });
  };

  const getResultObject = (): object => {
    return {
      dictionary,
      name,
      speed,
      speedMultiplier,
      duration,
      flag,
      playbackRate,
      lockX: false,
      lockY: false,
      lockZ: false,
    };
  };

  useEffect(() => {
    console.log("anim player was created");

    (async () => {
      const response = await fetch("/dataset/animations.txt");
      const text: string = await response.text();

      const localAnimations = {};
      const lines = text.split("\n");
      lines.forEach((line) => {
        const [localDictionary, localName] = line.split(" ");

        const check = (v) => (typeof v === "string" || v instanceof String) && v.length > 0;

        if (!check(localDictionary) || !check(localName)) {
          console.log({ localDictionary, localName, line });
          return;
        }

        if (!(localDictionary in localAnimations)) {
          localAnimations[localDictionary] = [];
        }

        localAnimations[localDictionary].push(localName);
      });

      setAnimations(localAnimations);
    })();
  }, []);

  return (
    <div>
      <div ref={portalRef} />

      <Card className="absolute bottom-10 right-10 p-5 w-[25rem]" variant="solid-surface-gradient">
        <form onSubmit={handleSubmit}>
          <div className="grid grid-cols-12 gap-3">
            <div className="col-span-full">
              <div className="mb-1">
                <Button
                  size="3"
                  type="button"
                  className="w-full"
                  onClick={() => setUsesDictionary(!usesDictionary)}
                  color={usesDictionary ? "green" : "red"}
                >
                  словарик
                </Button>
              </div>
              <div className="mb-2">
                {usesDictionary ? (
                  dictionary && portalRef && portalRef.current ? (
                    <AnimationDictionaryComponent
                      setDictionary={setDictionary}
                      setName={setName}
                      animations={animations}
                      portalRef={portalRef}
                    />
                  ) : (
                    <div className="text-center">Загрузка словаря</div>
                  )
                ) : (
                  ""
                )}
              </div>
            </div>

            <div className="col-span-full">
              <div className="mb-1">Dictionary : Name</div>
              <TextField.Root
                size="3"
                name="composedName"
                onChange={(e) => setComposedName(e.target.value)}
                value={getComposedName()}
                required
                placeholder="dictionary : name"
              />
            </div>

            <div className="col-span-6">
              <div className="mb-1">Dictionary</div>
              <TextField.Root
                size="3"
                name="dictionary"
                onChange={(e) => setDictionary(e.target.value)}
                value={dictionary}
                required
                placeholder="dictionary"
              />
            </div>

            <div className="col-span-6">
              <div className="mb-1">Name</div>
              <TextField.Root
                size="3"
                name="name"
                onChange={(e) => setName(e.target.value)}
                value={name}
                required
                placeholder="name"
              />
            </div>

            <div className="col-span-6">
              <div className="mb-1">Speed</div>
              <TextField.Root
                size="3"
                name="speed"
                type="text"
                onChange={createChangeNumber(setSpeed, false)}
                onBlur={createBlurChangeNumber(speed, setSpeed)}
                value={speed}
                required
                placeholder="speed"
              />
            </div>

            <div className="col-span-6">
              <div className="mb-1">SpeedMultiplayer</div>
              <TextField.Root
                size="3"
                name="speedMultiplayer"
                type="text"
                onChange={createChangeNumber(setSpeedMultiplier, false)}
                onBlur={createBlurChangeNumber(speedMultiplier, setSpeedMultiplier)}
                value={speedMultiplier}
                required
                placeholder="speedMultiplier"
              />
            </div>

            <div className="col-span-4">
              <div className="mb-1">Duration</div>
              <TextField.Root
                size="3"
                name="duration"
                type="text"
                onChange={createChangeNumber(setDuration, false)}
                onBlur={createBlurChangeNumber(duration, setDuration)}
                value={duration}
                required
                placeholder="duration"
              />
            </div>

            <div className="col-span-4">
              <div className="mb-1">Flag</div>
              <TextField.Root
                size="3"
                name="flag"
                type="text"
                onChange={createChangeNumber(setFlag, false)}
                onBlur={createBlurChangeNumber(flag, setFlag)}
                value={flag}
                required
                placeholder="flag"
              />
            </div>

            <div className="col-span-4">
              <div className="mb-1">PlaybackRate</div>
              <TextField.Root
                size="3"
                name="playbackRate"
                type="text"
                onChange={createChangeNumber(setPlaybackRate, false)}
                onBlur={createBlurChangeNumber(playbackRate, setPlaybackRate)}
                value={playbackRate}
                required
                placeholder="playbackRate"
              />
            </div>

            <div className="col-span-6">
              <Button size="3" type="submit" className="w-full" color="green">
                Start
              </Button>
            </div>

            <div className="col-span-6">
              <Button size="3" type="button" className="w-full" onClick={() => stop()}>
                Stop
              </Button>
            </div>

            <div className="col-span-full">
              Result
              <TextField.Root
                size="3"
                name="content"
                type="text"
                disabled
                value={JSON.stringify(getResultObject())}
              />
            </div>
          </div>
        </form>
      </Card>
    </div>
  );
}

export default memo(AnimationPlayerContainer);
